// Copyright 2019 The MathWorks, Inc.
/**
 * Logs Widget UI Events for DDUX
 */
define(["dojo/_base/declare", "dojo/_base/lang", "dojo/dom", "dojo/on", "dijit/registry"], function (declare, lang, dom, on, registry) {

    var _logEventFunction;
    var _allowProductScopeOverride;
    var _listenersAdded;

    var eventsForType = {
        CheckBox: ["click"],
        ColorPickerButton: ["change"],
        ComboBox: ["dropdownclick", "change"],
        DropDownButton: ["dropdownclick"],
        GalleryPopupButton: ["click"],
        GalleryItem: ["click"],
        HorizontalSlider: ["change"],
        List: ["change"],
        ListItem: ["click"],
        ListItemWithCheckBox: ["click"],
        ListItemWithRadioButton: ["click"],
        ListItemWithTextField: ["change", "click"],
        ListItemWithPopup: [],
        PopupList: [],
        PushButton: ["click"],
        QABPushButton: ["click"],
        QABDropDownButton: ["dropdownclick"],
        QABSplitButton: ["click", "dropdownclick"],
        QABToggleButton: ["click"],
        QABToggleSplitButton: ["click", "dropdownclick"],
        RadioButton: ["click"],
        Spinner: ["change"],
        SplitButton: ["click", "dropdownclick"],
        ToolstripTab: ["click"],
        TextArea: ["change"],
        TextField: ["change"],
        ToggleButton: ["click"],
        ToggleGalleryItem: ["click"],
        ToggleSplitButton: ["click", "dropdownclick"]
    };

    var widgetCssClasses = ["mwPushButton", "mwDropDownButton", "mwMenuItem", "mwPopupMenuItem", "mwSplitButton", "mwToggleButton", "mwToggleSplitButton", "mwComboBox", "mwCheckBox", "mwRadioButton", "mwSpinner", "mwGalleryItem", "mwToggleGalleryItem", "mwTextField", "mwTextArea", "mwCheckBoxMenuItem", "mwRadioButtonMenuItem", "mwTextFieldMenuItem", "mwListBox", "mwColorPickerButton", "mwGridPickerButton"];

    var USAGE_ATTRIBUTE_NAME = "data-usage-logging";

    return {
        /**
         * @param allowProductScopeOverride {boolean} should be set true if
         *        products and scopes other than those specified above can exist in the same window.
         *        In this case the WidgetLogger will look for annotations on the DOM that can be used
         *        to override the default product and scope.
         * @param logEventFunction {function} the function to be called to log events
         */
        setup: function setup(defaultProperties, logEventFunction) {
            _uiEventDefaults = defaultProperties;
            _logEventFunction = logEventFunction;
        },

        /**
         * Logs a UI event associated with a given DOM node
         *
         * @param eventData {eventType: string, elementId: string, elementType: string}
         *                          the event data must include the first two of the above properties but may also include any
         *                          number of additional string-valued properties.
         * @param targetNode {Node} the DOM node associated with the event.  For a click this should be the node that was
         *                          clicked, while for a key event it should be the node to which the key event was targeted.
         *                          The targetNode will be used to determine whether the event should be logged and, if so, what
         *                          product, scope and subscope it should be associated with.  If a targetNode is not provided
         *                          the event will be associated with the default product and scope specified upon UsageLogger start.
         * @param immediate {boolean} if true the event will be logged immediately.  Otherwise logging may be deferred.
         */
        logEvent: function logEvent(eventData, targetNode, immediate) {
            var that = this;
            var doLogEvent = function doLogEvent() {
                if (targetNode) {
                    attributes = that._getLoggingAttributes(targetNode);
                    if (attributes.enabled) {
                        delete attributes.enabled;
                    } else {
                        return;
                    }
                    lang.mixin(eventData, attributes);
                }
                _logEventFunction(eventData, undefined, true);
            };
            if (immediate) {
                doLogEvent();
            } else {
                setTimeout(doLogEvent, 0);
            }
        },

        /**
         * Tags the given node to allow event logging from it and all of its children, except children for which
         * logging has been explicitly disabled.
         *
         * @param node {Node} the DOM node from which logging is being enabled
         * @param subscope {string} optional subscope to be included in event data originating from node or its children
         */
        enableLoggingFromNode: function enableLoggingFromNode(node, subscope) {
            this._addLoggingAttributes(node, { subscope: subscope, enabled: true });

            // Don't bother adding listeners until some logging has been enabled
            if (!_listenersAdded) {
                this._addListeners();
                _listenersAdded = true;
            }
        },

        /**
         * Tags the given node to disallow event logging from it and all of its children, except children for which
         * logging has been explicitly enabled.
         *
         * @param node {Node} the DOM node from which logging is being disabled
         */
        disableLoggingFromNode: function disableLoggingFromNode(node) {
            this._addLoggingAttributes(node, { enabled: false });
        },

        /**
         * Tags the given node with product and scope strings.  These strings will override the default values specified upon
         * UsageLogger start.  Events originating from the node or any of its children will be associated with the specified
         * product and scope values rather than the default values.
         *
         * @param node {Node} the DOM node to be associated with the specified product and scope
         * @param product {string} the product string to be included in event data
         * @param scope {string} the scope string to be included in event data
         */
        overrideProductAndScope: function overrideProductAndScope(node, product, scope) {
            this._addLoggingAttributes(node, { product: product, scope: scope });
        },

        _addListeners: function _addListeners() {
            var that = this;
            var handlingClick = false; // used to prevent multiple logs for a single click
            window.addEventListener("click", function (e) {
                if (!handlingClick) {
                    handlingClick = true;
                    that._logDomEvent(e, "click");
                    setTimeout(function () {
                        handlingClick = false;
                    }, 0);
                }
            }, true); // Respond on capture so that click is logged before any subsequently triggered
            // events (see geck 2135910)
            /*
            on(window, "click", function(e) {
                that._logDomEvent(e, "click");
            });
            */
            on(window, "change", function (e) {
                that._logDomEvent(e, "change");
            });
            on(window, "dropdownclick", function (e) {
                that._logDomEvent(e, "dropdownclick");
            });
        },

        _addLoggingAttributes: function _addLoggingAttributes(node, attributes) {
            var updatedAttributes = attributes;
            var existingAttributes = node.getAttribute(USAGE_ATTRIBUTE_NAME);
            if (existingAttributes) {
                updatedAttributes = JSON.parse(existingAttributes);
                lang.mixin(updatedAttributes, attributes);
            }
            node.setAttribute(USAGE_ATTRIBUTE_NAME, JSON.stringify(updatedAttributes));
        },

        _getLoggingAttributes: function _getLoggingAttributes(node) {
            var popupInvoker = this._getPopupInvoker(node);
            if (popupInvoker) {
                node = popupInvoker;
            }
            var attributes = {};
            while (node && (attributes.enabled === undefined || _allowProductScopeOverride && !(attributes.product && attributes.scope))) {
                var attributeString = node.getAttribute ? node.getAttribute(USAGE_ATTRIBUTE_NAME) : undefined;
                if (attributeString) {
                    var nodeAttributes = JSON.parse(attributeString);
                    if (!attributes.product && nodeAttributes.product) {
                        attributes.product = nodeAttributes.product;
                    }
                    if (!attributes.scope && nodeAttributes.scope) {
                        attributes.scope = nodeAttributes.scope;
                    }
                    if (!attributes.subscope && nodeAttributes.subscope) {
                        attributes.subscope = nodeAttributes.subscope;
                    }
                    if (!attributes.enabled && nodeAttributes.enabled !== undefined) {
                        attributes.enabled = nodeAttributes.enabled;
                    }
                }
                node = node.parentNode;
            }
            return attributes;
        },

        _logDomEvent: function _logDomEvent(event, eventType) {
            var nodeInfo = this._getNodeInfoFromEvent(event);
            if (nodeInfo && this._isEventOfInterest(nodeInfo.type, eventType)) {
                var usageEvent = {
                    eventType: event.keyStroke ? "keypress" : "click",
                    elementId: nodeInfo.tag,
                    elementType: this._translateElementType(nodeInfo.type)
                };
                if (usageEvent.elementType === "GalleryItem" || usageEvent.elementType === "ToggleGalleryItem") {
                    // Map gallery element type to match established convention which depends on
                    // which gallery view is active.
                    var widget = registry.getEnclosingWidget(nodeInfo.node);
                    if (widget) {
                        usageEvent.elementType = widget.displayState === "icon_view" ? "button" : "list_item";
                    }
                }
                if (event.keyStroke) {
                    usageEvent.keyStroke = event.keyStroke;
                }
                this.logEvent(usageEvent, nodeInfo.node);
            }
        },

        _getNodeInfoFromEvent: function _getNodeInfoFromEvent(event) {
            // Walk up the dom from the event target, looking for a node with data-tag and data-type
            // attributes
            var node = event.target;
            while (node && node.getAttribute) {
                var tag = node.getAttribute("data-tag");
                if (tag) {
                    var type = node.getAttribute("data-type");
                    if (!type) {
                        type = this._getWidgetTypeFromCss(node);
                    }
                    if (type) {
                        break;
                    }
                }
                node = node.parentNode;
            }
            return node ? { node: node, tag: tag, type: type } : null;
        },

        _getWidgetTypeFromCss: function _getWidgetTypeFromCss(node) {
            var classList = node.classList;
            if (classList.contains("mwWidget")) {
                for (var i = 0; i < widgetCssClasses.length; i++) {
                    var cssClass = widgetCssClasses[i];
                    if (classList.contains(cssClass)) {
                        return cssClass.substring(2); // Drop the "mw" prefix to get the type
                    }
                }
            }
            return null;
        },

        _isEventOfInterest: function _isEventOfInterest(nodeType, eventType) {
            var typesOfInterest = eventsForType[nodeType];
            return typesOfInterest && typesOfInterest.indexOf(eventType) >= 0;
        },

        _getPopupInvoker: function _getPopupInvoker(node) {
            while (node && node.classList) {
                if (node.classList.contains("dijitPopup")) {
                    var parentId = node.getAttribute("dijitPopupParent");
                    if (parentId) {
                        var parent = dom.byId(parentId);
                        if (parent && parent.classList && parent.classList.contains("mwPopupMenuItem")) {
                            return this._getPopupInvoker(parent);
                        } else {
                            return parent;
                        }
                    }
                    break;
                }
                node = node.parentNode;
            }
            return null;
        },

        _translateElementType: function _translateElementType(jsElementType) {
            // The case values below correspond to definitions in MW/toolstrip/constants/TypeConstants but this
            // module cannot be referenced from here.
            // The return values correspond to an enumerated type defined in matlab/src/ddux/ddux_java/api/com/mathworks/ddux/ddux.java
            var javaElementType;
            switch (jsElementType) {
                case "ColorPickerButton":
                case "GridPickerButton":
                case "PushButton":
                case "SplitButton":
                case "ToggleButton":
                case "RadioButton":
                case "CheckBox":
                case "ToggleSplitButton":
                case "QABPushButton":
                case "QABSplitButton":
                case "QABToggleButton":
                case "QABToggleSplitButton":
                    javaElementType = "button";
                    break;
                case "DropDownButton":
                case "GalleryPopupButton":
                case "QABDropDownButton":
                    javaElementType = "drop_down";
                    break;
                case "ListItem":
                case "ListItemWithCheckBox":
                case "ListItemWithTextField":
                case "ListItemWithRadioButton":
                    javaElementType = "list_item";
                    break;
                case "ComboBox":
                    javaElementType = "combo_box";
                    break;
                case "HorizontalSlider":
                    javaElementType = "slider";
                    break;
                case "Spinner":
                    javaElementType = "spinner";
                    break;
                case "TextField":
                    javaElementType = "text_field";
                    break;
                case "TextArea":
                    javaElementType = "text_area";
                    break;
                case "ToolstripTab":
                    javaElementType = "toolstrip_tab";
                    break;
                default:
                    // Pass through other types and let the Java code deal with them.
                    javaElementType = jsElementType;
            }
            return javaElementType;
        }
    };
});

